class AdornyuMenu extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.button = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    this.closeButton = this.querySelector('[data-menu-close]');
    if (!this.button || !this.panel) return;
    this.button.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close(true));
    this.panel.querySelectorAll('a').forEach(link => link.addEventListener('click', () => this.close()));
    this.panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
      if (event.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll('a[href], button:not(:disabled)')];
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    this.onResize = () => { if (window.innerWidth > 749) this.close(); };
    window.addEventListener('resize', this.onResize);
  }
  disconnectedCallback() { window.removeEventListener('resize', this.onResize); document.body.classList.remove('menu-open'); }
  toggle() {
    if (this.button.getAttribute('aria-expanded') === 'true') { this.close(true); return; }
    this.querySelectorAll('details[open]').forEach(d => { d.open = false; });
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    document.body.classList.add('menu-open');
    this.closeButton?.focus({ preventScroll: true });
  }
  close(restoreFocus = false) {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    document.body.classList.remove('menu-open');
    if (restoreFocus) this.button.focus({ preventScroll: true });
  }
}

class AdornyuProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.select = this.querySelector('[data-variant-select]');
    this.price = this.querySelector('[data-product-price]');
    this.submit = this.querySelector('[data-add-to-cart]');
    this.availability = this.querySelector('[data-availability]');
    this.variantButtons = [...this.querySelectorAll('[data-variant-button]')];
    this.media = [...this.querySelectorAll('[data-product-media]')];
    this.gallery = this.querySelector('[data-gallery]');
    this.controls = this.querySelector('[data-gallery-controls]');
    this.previous = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.counter = this.querySelector('[data-gallery-count]');
    this.selectedVariant = this.querySelector('[data-selected-variant]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.querySelectorAll('[data-quantity-change]').forEach(button => {
      button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.quantityChange)));
    });
    this.previous?.addEventListener('click', () => this.moveImage(-1));
    this.next?.addEventListener('click', () => this.moveImage(1));
    this.gallery?.addEventListener('scroll', () => this.updateGalleryControls(), { passive: true });
    this.gallery?.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); this.moveImage(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    this.resizeObserver = new ResizeObserver(() => this.updateGalleryControls());
    if (this.gallery) this.resizeObserver.observe(this.gallery);
    if (this.select) {
      this.select.addEventListener('change', () => this.onVariantChange());
      this.variantButtons.forEach(button => button.addEventListener('click', () => {
        this.select.value = button.dataset.variantId; this.onVariantChange();
      }));
      this.updateMedia(this.select.options[this.select.selectedIndex].textContent.trim());
    } else { this.updateGalleryControls(); }
  }
  disconnectedCallback() { this.resizeObserver?.disconnect(); }
  onVariantChange() {
    const option = this.select.options[this.select.selectedIndex];
    const available = option.dataset.available === 'true';
    if (this.price && option.dataset.price) this.price.textContent = option.dataset.price;
    if (this.submit) {
      this.submit.disabled = !available;
      this.submit.textContent = available ? this.submit.dataset.availableLabel : this.submit.dataset.soldLabel;
    }
    if (this.availability) this.availability.hidden = available;
    if (this.selectedVariant) this.selectedVariant.textContent = option.textContent.trim();
    this.variantButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.variantId === this.select.value)));
    this.updateMedia(option.textContent.trim());
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.select.value);
    window.history.replaceState({}, '', url);
  }
  updateMedia(variantTitle) {
    if (!this.media.length) return;
    const needle = ` in ${variantTitle.toLowerCase()},`;
    const matches = this.media.filter(frame => (frame.dataset.mediaAlt || '').toLowerCase().includes(needle));
    this.media.forEach(frame => { frame.hidden = matches.length > 0 && !matches.includes(frame); });
    const first = this.media.find(frame => !frame.hidden);
    this.media.forEach(frame => frame.classList.toggle('adny-product__frame--lead', frame === first));
    const image = first?.querySelector('img');
    if (image) image.loading = 'eager';
    if (this.gallery) this.gallery.scrollLeft = 0;
    this.updateGalleryControls();
  }
  updateGalleryControls() {
    if (!this.gallery || !this.controls) return;
    const count = this.media.filter(frame => !frame.hidden).length;
    this.controls.hidden = count < 2;
    const index = Math.max(0, Math.min(count - 1, Math.round(this.gallery.scrollLeft / (this.gallery.clientWidth || 1))));
    if (this.counter) this.counter.textContent = `${index + 1} / ${count}`;
    if (this.previous) this.previous.disabled = index === 0;
    if (this.next) this.next.disabled = index >= count - 1;
  }
  moveImage(direction) {
    if (!this.gallery) return;
    const count = this.media.filter(frame => !frame.hidden).length;
    const index = Math.round(this.gallery.scrollLeft / (this.gallery.clientWidth || 1));
    const target = Math.max(0, Math.min(count - 1, index + direction));
    this.gallery.scrollTo({ left: target * this.gallery.clientWidth, behavior: 'instant' });
    this.updateGalleryControls();
  }
  changeQuantity(delta) {
    if (!this.quantityInput) return;
    this.quantityInput.value = Math.max(1, (Number(this.quantityInput.value) || 1) + delta);
  }
}
customElements.define('adornyu-menu', AdornyuMenu);
customElements.define('adornyu-product', AdornyuProduct);
